import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider, AdvancedModeProvider } from "@/contexts/ThemeContext";
import { AuthProvider } from "@/contexts/auth/AuthProvider";
import { SiteProvider } from "@/contexts/site";
import { SentryErrorBoundary, trackPageView } from "@/lib/sentry";
import { Routes, Route, Navigate, useLocation, useNavigationType, useParams } from "react-router-dom";
import { useEffect, Suspense, lazy } from "react";
import { useHealthIndicator } from '@/hooks/useHealthIndicator';
import { useVersionCheck } from '@/hooks/useVersionCheck';
import { useAuth } from '@/contexts/auth';
import { useNavigate } from 'react-router-dom';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import usePerformanceMonitor from '@/hooks/usePerformanceMonitor';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { MinimalAppSkeleton } from '@/components/ui/app-skeleton';
import { GlobalLoadingIndicator } from '@/components/ui/global-loading-indicator';
import { useBackendFrontendVersion } from '@/hooks/useBackendFrontendVersion';
import { setFrontendCompatibilityStatus } from '@/lib/api/core';

// Lazy load page components for better performance
const Login = lazy(() => import('@/pages/Login'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const CustomerSelect = lazy(() => import('@/pages/CustomerSelect'));
const SiteCreatePage = lazy(() => import('@/pages/sites/SiteCreatePage'));
const SiteEditPage = lazy(() => import('@/pages/sites/SiteEditPage'));
const SiteDetailPage = lazy(() => import('@/pages/sites/SiteDetailPage'));
const AddressPage = lazy(() => import('@/pages/addresses/AddressPage'));
const AddressDetailPage = lazy(() => import('@/pages/addresses/AddressDetailPage'));
const FloorsPage = lazy(() => import('@/pages/floors/FloorsPage'));
const FloorDetailPage = lazy(() => import('@/pages/floors/FloorDetailPage'));
const RoomsPage = lazy(() => import('@/pages/rooms/RoomsPage'));
const RoomDetailPage = lazy(() => import('@/pages/rooms/RoomDetailPage'));
const DoorsPage = lazy(() => import('@/pages/doors/DoorsPage'));
const DoorDetailPage = lazy(() => import('@/pages/doors/DoorDetailPage'));
const TenantsPage = lazy(() => import('@/pages/tenants/TenantsPage'));
const TenantDetailPage = lazy(() => import('@/pages/tenants/TenantDetailPage'));
const TenantCreatePage = lazy(() => import('@/pages/tenants/TenantCreatePage'));
const TenantEditPage = lazy(() => import('@/pages/tenants/TenantEditPage'));
const UsersPage = lazy(() => import('@/pages/users/UsersPage'));
const UserDetailPage = lazy(() => import('@/pages/users/UserDetailPage'));
const AxemaDashboard = lazy(() => import('@/pages/axema/AxemaDashboard'));
const AxemaSystemDetailPage = lazy(() => import('@/pages/axema/AxemaSystemDetailPage'));
const CustomersPage = lazy(() => import('@/pages/customers/CustomersPage'));
const TimelinePage = lazy(() => import('@/pages/timeline/TimelinePage'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const NoAccess = lazy(() => import('@/pages/NoAccess'));

// Network pages
const NetworksPage = lazy(() => import('@/pages/networks/NetworksPage'));
const NetworkDetailPage = lazy(() => import('@/pages/networks/NetworkDetailPage'));
const NetworkCreatePage = lazy(() => import('@/pages/networks/NetworkCreatePage'));
const NetworkEditPage = lazy(() => import('@/pages/networks/NetworkEditPage'));
const SpeedLimitsPage = lazy(() => import('@/pages/networks/SpeedLimitsPage'));
const SpeedLimitDetailPage = lazy(() => import('@/pages/networks/SpeedLimitDetailPage'));
const SpeedLimitCreatePage = lazy(() => import('@/pages/networks/SpeedLimitCreatePage'));
const SpeedLimitEditPage = lazy(() => import('@/pages/networks/SpeedLimitEditPage'));
const ExternalIPsPage = lazy(() => import('@/pages/external-ips/ExternalIPsPage'));
const ExternalIPDetailPage = lazy(() => import('@/pages/external-ips/ExternalIPDetailPage'));
const ExternalIPCreatePage = lazy(() => import('@/pages/external-ips/ExternalIPCreatePage'));

// Wireless pages
const WirelessPage = lazy(() => import('@/pages/wireless/WirelessPage'));
const WirelessDetailPage = lazy(() => import('@/pages/wireless/WirelessDetailPage'));
const WirelessCreatePage = lazy(() => import('@/pages/wireless/WirelessCreatePage'));
const WirelessEditPage = lazy(() => import('@/pages/wireless/WirelessEditPage'));
const WirelessCredentialsPage = lazy(() => import('@/pages/wireless/WirelessCredentialsPage'));

// Outlet pages
const OutletsPage = lazy(() => import('@/pages/outlets/OutletsPage'));
const OutletDetailPage = lazy(() => import('@/pages/outlets/OutletDetailPage'));

// Doorbell pages
const DoorbellsPage = lazy(() => import('@/pages/doorbell/DoorbellsPage'));
const DoorbellDetailPage = lazy(() => import('@/pages/doorbell/DoorbellDetailPage'));
const PresetMessagesPage = lazy(() => import('@/pages/doorbell/PresetMessagesPage'));
const DoorbellUpsertPage = lazy(() => import('@/pages/doorbell/DoorbellUpsertPage'));

// Home Assistant pages
const HomeAssistantDashboard = lazy(() => import('@/pages/hass/HomeAssistantDashboard'));
const InstanceDetailPage = lazy(() => import('@/pages/hass/InstanceDetailPage'));
const DevicesListPage = lazy(() => import('@/pages/hass/DevicesListPage'));
const HassDeviceDetailPage = lazy(() => import('@/pages/hass/DeviceDetailPage'));
const HassEntityDetailPage = lazy(() => import('@/pages/hass/EntityDetailPage'));
const HassAreaDetailPage = lazy(() => import('@/pages/hass/AreaDetailPage'));

// Papercut pages
const PapercutDashboard = lazy(() => import('@/pages/papercut/PapercutDashboard'));
const ManageSystemsPage = lazy(() => import('@/pages/papercut/ManageSystemsPage'));
const HassFloorDetailPage = lazy(() => import('@/pages/hass/FloorDetailPage'));
const ManageInstancesPage = lazy(() => import('@/pages/hass/ManageInstancesPage'));
const SyncLogsPage = lazy(() => import('@/pages/hass/SyncLogsPage'));
const WisenetDashboard = lazy(() => import('@/pages/wisenet/WisenetDashboard'));
const WisenetManageSystemsPage = lazy(() => import('@/pages/wisenet/ManageSystemsPage'));
const WisenetSystemDetailPage = lazy(() => import('@/pages/wisenet/WisenetSystemDetailPage'));

// UniFi
const UnifiRFDashboard = lazy(() => import('@/pages/unifi-rf/UnifiRFDashboard'));
const RFScanDetailPage = lazy(() => import('@/pages/unifi-rf/RFScanDetailPage'));

// Mikrotik pages - lazy loaded
const MikrotikDevicesPage = lazy(() => import('@/pages/mikrotik/MikrotikDevicesPage'));
const MikrotikDeviceDetailPage = lazy(() => import('@/pages/mikrotik/MikrotikDeviceDetailPage'));
const MikrotikDeviceCreatePage = lazy(() => import('@/pages/mikrotik/MikrotikDeviceCreatePage'));
const MikrotikDeviceEditPage = lazy(() => import('@/pages/mikrotik/MikrotikDeviceEditPage'));
const MikrotikDeviceConfigPage = lazy(() => import('@/pages/mikrotik/MikrotikDeviceConfigPage'));
const MikrotikInterfaceDetailPage = lazy(() => import('@/pages/mikrotik/MikrotikInterfaceDetailPage'));

// Arista pages - lazy loaded
const AristaDevicesPage = lazy(() => import('@/pages/arista/AristaDevicesPage'));
const AristaDeviceDetailPage = lazy(() => import('@/pages/arista/AristaDeviceDetailPage'));
const AristaDeviceCreatePage = lazy(() => import('@/pages/arista/AristaDeviceCreatePage'));
const AristaDeviceEditPage = lazy(() => import('@/pages/arista/AristaDeviceEditPage'));
const AristaDeviceConfigPage = lazy(() => import('@/pages/arista/AristaDeviceConfigPage'));
const AristaInterfaceDetailPage = lazy(() => import('@/pages/arista/AristaInterfaceDetailPage'));

// Client pages - lazy loaded
const ClientDetailPage = lazy(() => import('@/pages/clients/ClientDetailPage'));
const ClientsListPage = lazy(() => import('@/pages/clients/ClientsListPage'));
// Network diagnostics - Diagnostic Sessions (replaces Connectivity tests + Speed test)
const DiagnosticSessionsPage = lazy(() => import('@/pages/network/DiagnosticSessionsPage'));
const DiagnosticSessionDetailPage = lazy(() => import('@/pages/network/DiagnosticSessionDetailPage'));

// Auth components
import { AuthGuard } from '@/components/auth/AuthGuard';
import { CustomerRedirect } from '@/components/auth/CustomerRedirect';
import { SuperAdminGuard } from '@/components/auth/SuperAdminGuard';

// Dev components
import { ApiEnvironmentSwitcher } from '@/components/dev';

// Track route changes for better session replays
const RouteChangeTracker = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  
  // Scroll to top on route change
  useScrollToTop();
  
  useEffect(() => {
    const pageName = location.pathname.split('/').filter(Boolean).join('/') || 'root';
    const pageTitle = document.title || pageName;
    
    trackPageView(location.pathname, pageTitle);
  }, [location, navigationType]);
  
  return null;
};

// Redirect component for /users/:userId to /user/:userId
const UserDetailRedirect = () => {
  const { userId } = useParams<{ userId: string }>();
  return <Navigate to={`/user/${userId}`} replace />;
};

function useCrossTabLoginSync() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === 'login-event') {
        // If not authenticated, reload the page to trigger login flow
        if (!isAuthenticated) {
          window.location.reload();
        }
        // If already authenticated, do nothing (user is already logged in)
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [isAuthenticated, navigate]);
}

// Wrap the app with necessary providers
const App = () => {
  useHealthIndicator({ showToast: true });
  useVersionCheck();
  useCrossTabLoginSync();
  usePerformanceMonitor(); // Add performance monitoring
  const { data: versionInfo } = useBackendFrontendVersion();

  useEffect(() => {
    setFrontendCompatibilityStatus(versionInfo?.compatibility_status ?? 'unknown');
  }, [versionInfo?.compatibility_status]);
  
  return (
    <SentryErrorBoundary>
      <ThemeProvider>
        <AdvancedModeProvider>
          <AuthProvider>
            <SiteProvider>
              <TooltipProvider>
                <GlobalLoadingIndicator />
                {versionInfo?.compatibility_status === 'incompatible' && (
                  <div className="fixed top-0 left-0 right-0 z-[60] bg-destructive text-destructive-foreground px-3 py-2 text-sm text-center">
                    Frontend {typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev'} is incompatible with backend {versionInfo?.backend_version ?? 'unknown'}.
                    Update frontend to at least {versionInfo?.minimum_frontend_version ?? 'required version'} to enable changes.
                  </div>
                )}
                <RouteChangeTracker />
                <Suspense fallback={<MinimalAppSkeleton />}>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/no-access" element={<NoAccess />} />
                    
                    {/* Customer selection route - Unified under /select-customer */}
                    <Route path="/select-customer" element={<CustomerRedirect />} />
                    
                    {/* Protected routes */}
                    <Route element={<AuthGuard />}>
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/addresses" element={<AddressPage />} />
                      
                      {/* Site routes - ensuring consistent paths */}
                      <Route path="/site" element={<SiteDetailPage />} />
                      <Route path="/site/create" element={<SiteCreatePage />} />
                      <Route path="/site/edit" element={<SiteEditPage />} />
                      
                      
                      {/* Detail pages with updated paths */}
                      <Route path="/address/:addressId" element={<AddressDetailPage />} />
                      <Route path="/floors" element={<FloorsPage />} />
                      <Route path="/floor/:floorId" element={<FloorDetailPage />} />
                      <Route path="/rooms" element={<RoomsPage />} />
                      <Route path="/room/:roomId" element={<RoomDetailPage />} />
                      <Route path="/doors" element={<DoorsPage />} />
                      <Route path="/door/:doorId" element={<DoorDetailPage />} />
                      
                      <Route path="/tenants" element={<TenantsPage />} />
                      <Route path="/tenant/:tenantId" element={<TenantDetailPage />} />
                      <Route path="/tenants/create" element={<TenantCreatePage />} />
                      <Route path="/tenant/:tenantId/edit" element={<TenantEditPage />} />
                      <Route path="/users" element={<UsersPage />} />
                      <Route path="/users/:userId" element={<UserDetailRedirect />} />
                      <Route path="/user/:userId" element={<UserDetailPage />} />
                      <Route path="/axema" element={<AxemaDashboard />} />
                      <Route path="/axema/systems/:systemId" element={<AxemaSystemDetailPage />} />
                      {/* Redirect old Axema routes to new dashboard */}
                      <Route path="/axema/tools" element={<Navigate to="/axema" replace />} />
                      <Route path="/axema/tools/link-tenants" element={<Navigate to="/axema" replace />} />
                      <Route path="/axema/tools/unmatched-orgs" element={<Navigate to="/axema" replace />} />
                      <Route path="/axema/tools/import-persons" element={<Navigate to="/axema" replace />} />
                      <Route path="/customers" element={<CustomersPage />} />
                      
                      {/* Network routes */}
                      <Route path="/networks" element={<NetworksPage />} />
                      <Route path="/network/:networkId" element={<NetworkDetailPage />} />
                      <Route path="/networks/create" element={<NetworkCreatePage />} />
                      <Route path="/network/:networkId/edit" element={<NetworkEditPage />} />
                      <Route path="/networks/speed-limits" element={<SpeedLimitsPage />} />
                      <Route path="/networks/speed-limit/:speedLimitId" element={<SpeedLimitDetailPage />} />
                      <Route path="/networks/speed-limits/create" element={<SpeedLimitCreatePage />} />
                      <Route path="/networks/speed-limit/:speedLimitId/edit" element={<SpeedLimitEditPage />} />
                      <Route path="/external-ips" element={<ExternalIPsPage />} />
                      <Route path="/external-ip/:externalIPId" element={<ExternalIPDetailPage />} />
                      <Route path="/external-ips/create" element={<ExternalIPCreatePage />} />
                      <Route path="/networks/external-ips" element={<ExternalIPsPage />} />
                      
                      {/* Wireless routes */}
                      <Route path="/wireless" element={<WirelessPage />} />
                      <Route path="/wireless/:wirelessId" element={<WirelessDetailPage />} />
                      <Route path="/wireless/create" element={<WirelessCreatePage />} />
                      <Route path="/wireless/:wirelessId/edit" element={<WirelessEditPage />} />
                      <Route path="/wireless/:wirelessId/credentials" element={<WirelessCredentialsPage />} />
                      
                      {/* Outlet routes */}
                      <Route path="/outlets" element={<OutletsPage />} />
                      <Route path="/outlet/:outletId" element={<OutletDetailPage />} />
                      
                      {/* Doorbell routes */}
                      <Route path="/doorbells" element={<DoorbellsPage />} />
                      <Route path="/doorbell/preset-messages" element={<PresetMessagesPage />} />
                      <Route path="/doorbell/create" element={<DoorbellUpsertPage />} />
                      <Route path="/doorbell/:id" element={<DoorbellDetailPage />} />
                      <Route path="/doorbell/:id/edit" element={<DoorbellUpsertPage />} />
                      
                      {/* Home Assistant routes */}
                      <Route path="/hass" element={<HomeAssistantDashboard />} />
                      <Route path="/hass/instances/:id" element={<InstanceDetailPage />} />
                      <Route path="/hass/devices/:deviceId" element={<HassDeviceDetailPage />} />
                      <Route path="/hass/entities/:entityId" element={<HassEntityDetailPage />} />
                      <Route path="/hass/areas/:areaId" element={<HassAreaDetailPage />} />
                      <Route path="/hass/floors/:floorId" element={<HassFloorDetailPage />} />
                      <Route path="/hass/manage-instances" element={<ManageInstancesPage />} />
                      <Route path="/hass/devices" element={<DevicesListPage />} />
                      <Route path="/hass/sync-logs" element={<SyncLogsPage />} />
                      
                      {/* Papercut routes */}
                      <Route path="/papercut" element={<PapercutDashboard />} />
                      <Route path="/papercut/systems" element={<ManageSystemsPage />} />

                      {/* Wisenet routes */}
                      <Route path="/wisenet" element={<WisenetDashboard />} />
                      <Route path="/wisenet/systems" element={<WisenetManageSystemsPage />} />
                      <Route path="/wisenet/systems/:systemId" element={<WisenetSystemDetailPage />} />

                      {/* UniFi routes */}
                      <Route path="/unifi" element={<UnifiRFDashboard />} />
                      <Route path="/unifi/scans/:scanId" element={<RFScanDetailPage />} />

                      {/* Timeline routes */}
                      <Route path="/timeline" element={<TimelinePage />} />
                      
                      {/* Mikrotik routes - only for superadmins */}
                      <Route 
                        path="/mikrotik/devices" 
                        element={
                          <SuperAdminGuard>
                            <MikrotikDevicesPage />
                          </SuperAdminGuard>
                        } 
                      />
                      <Route 
                        path="/mikrotik/devices/:id" 
                        element={
                          <SuperAdminGuard>
                            <MikrotikDeviceDetailPage />
                          </SuperAdminGuard>
                        } 
                      />
                      <Route 
                        path="/mikrotik/devices/create" 
                        element={
                          <SuperAdminGuard>
                            <MikrotikDeviceCreatePage />
                          </SuperAdminGuard>
                        } 
                      />
                      <Route 
                        path="/mikrotik/devices/:id/edit" 
                        element={
                          <SuperAdminGuard>
                            <MikrotikDeviceEditPage />
                          </SuperAdminGuard>
                        } 
                      />
                      <Route 
                        path="/mikrotik/devices/:id/config" 
                        element={
                          <SuperAdminGuard>
                            <MikrotikDeviceConfigPage />
                          </SuperAdminGuard>
                        } 
                      />
                      <Route 
                        path="/mikrotik/devices/:deviceId/interfaces/:interfaceId" 
                        element={
                          <SuperAdminGuard>
                            <MikrotikInterfaceDetailPage />
                          </SuperAdminGuard>
                        } 
                      />
                      
                      {/* Arista routes - only for superadmins */}
                      <Route 
                        path="/arista/devices" 
                        element={
                          <SuperAdminGuard>
                            <AristaDevicesPage />
                          </SuperAdminGuard>
                        } 
                      />
                      <Route 
                        path="/arista/devices/:id" 
                        element={
                          <SuperAdminGuard>
                            <AristaDeviceDetailPage />
                          </SuperAdminGuard>
                        } 
                      />
                      <Route 
                        path="/arista/devices/create" 
                        element={
                          <SuperAdminGuard>
                            <AristaDeviceCreatePage />
                          </SuperAdminGuard>
                        } 
                      />
                      <Route 
                        path="/arista/devices/:id/edit" 
                        element={
                          <SuperAdminGuard>
                            <AristaDeviceEditPage />
                          </SuperAdminGuard>
                        } 
                      />
                      <Route 
                        path="/arista/devices/:id/config" 
                        element={
                          <SuperAdminGuard>
                            <AristaDeviceConfigPage />
                          </SuperAdminGuard>
                        } 
                      />
                      <Route 
                        path="/arista/devices/:deviceId/interfaces/:interfaceId" 
                        element={
                          <SuperAdminGuard>
                            <AristaInterfaceDetailPage />
                          </SuperAdminGuard>
                        } 
                      />
                      
                      {/* Diagnostic session detail (must be before /diagnostic) */}
                      <Route
                        path="/diagnostic/session/:ref"
                        element={
                          <SuperAdminGuard>
                            <DiagnosticSessionDetailPage />
                          </SuperAdminGuard>
                        }
                      />
                      {/* Diagnostic (replaces Connectivity tests + Speed test) */}
                      <Route
                        path="/diagnostic"
                        element={
                          <SuperAdminGuard>
                            <DiagnosticSessionsPage />
                          </SuperAdminGuard>
                        }
                      />
                      <Route path="/speedtest" element={<Navigate to="/diagnostic" replace />} />
                      <Route path="/network-devices/connectivity-tests" element={<Navigate to="/diagnostic" replace />} />
                      <Route path="/diagnostic-sessions" element={<Navigate to="/diagnostic" replace />} />

                      {/* Network device routes */}
                      <Route
                        path="/network-devices"
                        element={
                          <SuperAdminGuard>
                            <ClientsListPage />
                          </SuperAdminGuard>
                        }
                      />
                      <Route 
                        path="/network-devices/:macAddress" 
                        element={
                          <SuperAdminGuard>
                            <ClientDetailPage />
                          </SuperAdminGuard>
                        } 
                      />
                      
                      {/* Redirects */}
                      <Route path="/" element={<CustomerRedirect />} />
                    </Route>
                    
                    {/* Catch-all route */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
                <Toaster />
                <ApiEnvironmentSwitcher />
                {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
              </TooltipProvider>
            </SiteProvider>
          </AuthProvider>
        </AdvancedModeProvider>
      </ThemeProvider>
    </SentryErrorBoundary>
  );
};

export default App;
